import { Star, Lightbulb, Fan, ArrowLeftRight } from 'lucide-react';
import Button from '../common/Button';

const BoardCard = ({ 
  board, 
  onReserve, 
  showReserveButton = true, 
  showFullDetails = false,
  showViewButton = true,
  className = '' 
}) => {
  const getBoardTypeColor = (type) => {
    const colors = {
      'tension': 'bg-slate-200 text-slate-900 border-slate-500',
      'moon': 'bg-purple-100 text-purple-800 border-purple-500',
      'kilter': 'bg-teal-100 text-teal-800 border-teal-500',
      'spray': 'bg-magenta-100 text-magenta-800 border-magenta-500',
      'default': 'bg-slate-100 text-slate-800 border-slate-400'
    };
    return colors[type?.toLowerCase()] || colors.default;
  };

  const getBoardStatus = () => {
    if (board.currentReservation) {
      return {
        status: 'occupied',
        message: `Occupied until ${board.currentReservation.endTime}`,
        color: 'bg-coral-100 text-coral-800 border-coral-500'
      };
    }

    return {
      status: 'available',
      message: 'Available',
      color: 'bg-teal-100 text-teal-800 border-teal-500'
    };
  };

  const boardStatus = getBoardStatus();

  return (
    <div className={`bg-white rounded-xl overflow-hidden transition-all hover:scale-[1.02] ${className} ${board.is_featured ? 'shadow-retro-hover border-2 border-magenta-500' : 'shadow-retro border-2 border-slate-900'}`}>
      {/* Board Image/Header */}
      <div className="relative">
        {board.image ? (
          <img
            className="w-full h-48 object-cover"
            src={board.image}
            alt={board.name}
          />
        ) : (
          <div className="w-full h-48 bg-gradient-to-br from-teal-100 via-purple-100 to-magenta-100 flex items-center justify-center border-b-2 border-slate-900">
            <svg className="h-16 w-16 text-slate-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          </div>
        )}

        {/* NEW Badge Ribbon */}
        {board.show_new_badge && (
          <div className="absolute top-0 left-0">
            <div className="bg-gradient-to-r from-sunshine-500 to-coral-500 text-white px-3 py-1 text-xs font-bold uppercase shadow-lg border-2 border-slate-900 transform -rotate-45 -translate-x-8 translate-y-4 w-32 text-center">
              NEW
            </div>
          </div>
        )}

        {/* Featured Star */}
        {board.is_featured && (
          <div className="absolute top-2 left-2">
            <span className="text-2xl" title="Featured Board">⭐</span>
          </div>
        )}

        {/* Status Badge */}
        <div className="absolute top-4 right-4">
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold border-2 ${boardStatus.color} shadow-retro`}>
            {boardStatus.message}
          </span>
        </div>

        {/* Board Type Badge */}
        <div className="absolute bottom-4 left-4">
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold border-2 ${getBoardTypeColor(board.type)} shadow-retro`}>
            {board.type}
          </span>
        </div>
      </div>

      {/* Board Details */}
      <div className="p-6">
        {/* Header */}
        <div className="mb-4">
          <h3 className="text-xl font-bold text-slate-900 mb-1">
            {board.name}
          </h3>

          {board.description && (
            <p className="text-slate-600 text-sm">
              {board.description}
            </p>
          )}
        </div>

        {/* Basic Stats */}
        <div className="space-y-2 mb-4 min-h-[120px]">
          {/* Location */}
          {(board.location_notes || board.location) && (
            <div className="flex items-center text-sm text-slate-600">
              <svg className="w-4 h-4 mr-2 flex-shrink-0 text-purple-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {board.location_notes || board.location || 'Main Floor'}
            </div>
          )}

          {/* Angle */}
          {(board.angle || board.is_adjustable_angle) && (
            <div className="flex items-center text-sm text-slate-600">
              <svg className="w-4 h-4 mr-2 flex-shrink-0 text-teal-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
              {board.is_adjustable_angle
                ? `${board.min_angle}° - ${board.max_angle}° (Adjustable)`
                : `${board.angle}° Angle`
              }
            </div>
          )}

          {/* Hold Sets */}
          {board.hold_sets && board.hold_sets.length > 0 && (
            <div className="flex items-start text-sm text-slate-600">
              <svg className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0 text-magenta-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
              </svg>
              <span className="flex-1">{board.hold_sets.join(', ')}</span>
            </div>
          )}

          {/* Features */}
          {board.features && (board.features.led || board.features.fan || board.features.mirrored) && (
            <div className="flex flex-wrap gap-2 mt-2">
              {board.features.led && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-sunshine-100 text-sunshine-800 border border-sunshine-500">
                  💡 LED
                </span>
              )}
              {board.features.fan && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-teal-100 text-teal-800 border border-teal-500">
                  🌀 Fan
                </span>
              )}
              {board.features.mirrored && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800 border border-purple-500">
                  🔄 Mirrored
                </span>
              )}
            </div>
          )}
        </div>

        {/* Extended Details (if showFullDetails is true) */}
        {showFullDetails && (
          <div className="space-y-4 mb-6">
            {/* Operating Hours */}
            {board.operatingHours && (
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-2">Operating Hours</h4>
                <div className="text-sm text-gray-600">
                  {board.operatingHours.map((hours, index) => (
                    <div key={index} className="flex justify-between">
                      <span>{hours.day}</span>
                      <span>{hours.open} - {hours.close}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Features */}
            {board.features && board.features.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-2">Features</h4>
                <div className="flex flex-wrap gap-2">
                  {board.features.map((feature, index) => (
                    <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {feature}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Recent Activity */}
            {board.recentReservations && (
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-2">Recent Activity</h4>
                <div className="text-sm text-gray-600">
                  <p>Last reserved: {board.lastReserved || 'Never'}</p>
                  <p>Total reservations this month: {board.monthlyReservations || 0}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex space-x-3">
          {showReserveButton && (
            <Button
              onClick={() => onReserve && onReserve(board)}
              className="flex-1 flex items-center justify-center"
              variant={boardStatus.status === 'available' ? 'primary' : 'secondary'}
            >
              {boardStatus.status === 'available' ? (
                <>
                  <svg className="w-4 h-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Reserve Time
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Book a Slot
                </>
              )}
            </Button>
          )}
          
          {showViewButton && (
            <Button
              onClick={() => window.location.href = `/board/${board.id}`}
              variant="outline"
              className="flex-shrink-0 flex items-center justify-center"
            >
              <svg className="w-4 h-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              View
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BoardCard;